import type { DesignScheme } from '@data/buildingProjects/DesignSchemes/designScheme';
import usePersistentStore, { getDesignSchemesState } from '@gui/store/persistent/persistentStore';
import {
    BoxDesignState,
    DeepPartial,
    DesignSchemeState,
    FloorBasedDesignState,
    GlobalDesignSchemeState,
    LayerKind,
    RendererBaseConfig,
    SchemeKey,
} from '@visualizer/types';

type UpdateDesignSchemesState = (updates: DeepPartial<GlobalDesignSchemeState>) => void;

/**
 * Keeps runtime DesignScheme instances connected to store-driven changes.
 * It owns the reactive subscriptions for renderer config and frame/layer changes,
 * and pushes back small derived UI state updates when runtime data changes.
 *
 */
export class DesignSchemeRuntimeSync {
    // =====================================
    // CLASS FIELDS
    // =====================================

    private schemeSubscriptions = new Map<string, Array<() => void>>();

    private readonly rendererStateSelectors = {
        [SchemeKey.FLOOR_BASED]: (state: DesignSchemeState) => state.floorBased,
        [SchemeKey.CONVEX_HULL]: (state: DesignSchemeState) => state.convexHull,
        [SchemeKey.ORIENTED_VOLUME]: (state: DesignSchemeState) => state.orientedVolume,
        [SchemeKey.BOX]: (state: DesignSchemeState) => state.box,
    } as const;

    private readonly rendererDefaultSelectors = {
        [SchemeKey.FLOOR_BASED]: (defaults: any) => defaults.floorBased,
        [SchemeKey.CONVEX_HULL]: (defaults: any) => defaults.convexHull,
        [SchemeKey.ORIENTED_VOLUME]: (defaults: any) => defaults.orientedVolume,
        [SchemeKey.BOX]: (defaults: any) => defaults.box,
    } as const;

    constructor(private readonly update: UpdateDesignSchemesState) {}

    // =====================================
    // PUBLIC API
    // =====================================

    public subscribeScheme(scheme: DesignScheme): void {
        const unsubscribes: Array<() => void> = [];

        unsubscribes.push(this.subscribeRendererConfigChanges(scheme));
        unsubscribes.push(this.subscribeLinkedLayerFrameChanges(scheme));
        unsubscribes.push(this.subscribeLinkedLayerPipelineExecutionChanges(scheme));

        this.schemeSubscriptions.set(scheme.getID(), unsubscribes);
    }

    public unsubscribeScheme(schemeId: string): void {
        const unsubscribes = this.schemeSubscriptions.get(schemeId);
        if (!unsubscribes) return;

        unsubscribes.forEach((fn) => fn());
        this.schemeSubscriptions.delete(schemeId);
    }

    public getRendererConfig(scheme: DesignScheme): RendererBaseConfig {
        const key = scheme.getRendererKey();
        const state = this.getSchemeState(scheme);
        const defaults = getDesignSchemesState().defaults;

        const stateSelector = this.rendererStateSelectors[key];
        const defaultSelector = this.rendererDefaultSelectors[key];

        return (state ? stateSelector(state) : undefined) ?? defaultSelector(defaults);
    }

    private getSchemeState(scheme: DesignScheme): DesignSchemeState | undefined {
        const store = getDesignSchemesState();

        const sets = store?.sets;
        if (!sets) return undefined;

        const setId = scheme.getDesignSchemeSet().getID();
        const fpId = scheme.getFootprintId();
        const schemeId = scheme.getID();

        return sets[setId]?.footprints[fpId]?.designSchemes[schemeId];
    }

    // =====================================
    // LISTENERS - TO HAVE REACTIVE DS SYNC
    // =====================================

    // 1) Updates DS when the store config (aka UI) changes:
    private subscribeRendererConfigChanges(scheme: DesignScheme): () => void {
        return usePersistentStore.subscribe(
            () => this.getRendererConfig(scheme),
            (newCfg, oldCfg) => {
                if (JSON.stringify(newCfg) === JSON.stringify(oldCfg)) return;
                if (scheme.isDisposed()) return;

                const renderer = scheme.getRenderer();
                if (!renderer) return;

                renderer.updateConfig(newCfg);
                scheme.update();
                if (scheme.isDisposed()) return;
                this.syncDerivedStateOnGenerationComplete(scheme);
            }
        );
    }

    // 2) Updates DS when any linked-layer frame changes:
    private subscribeLinkedLayerFrameChanges(scheme: DesignScheme): () => void {
        return usePersistentStore.subscribe(
            (s) => this.getLinkedLayerFrameSnapshot(scheme, s.layers.layerStates as Record<string, { segments?: any }>),
            (next, previous) => {
                const prev = previous ?? {};
                const changedLayerIds = Object.keys(next).filter((layerId) => {
                    const curr = next[layerId];
                    const old = prev[layerId];
                    return (
                        !old ||
                        curr.frameSelected !== old.frameSelected ||
                        curr.isAggregate !== old.isAggregate ||
                        curr.framesList !== old.framesList
                    );
                });
                this.refreshSchemeForChangedLayers(scheme, changedLayerIds);
            }
        );
    }

    // 2.1) Builds a snapshot of the frame (active) for all linked layers:
    private getLinkedLayerFrameSnapshot(scheme: DesignScheme, layerStates: Record<string, { segments?: any }>) {
        const snapshot: Record<string, { frameSelected: number | null; isAggregate: boolean | null; framesList: string }> = {};
        this.getWatchedLayerIds(scheme).forEach((layerId) => {
            const segments = layerStates[layerId]?.segments;
            snapshot[layerId] = {
                frameSelected: segments?.frameSelected ?? null,
                isAggregate: segments?.isAggregate ?? null,
                framesList: Array.isArray(segments?.framesList) ? segments.framesList.join(',') : '',
            };
        });
        return snapshot;
    }

    // 3) Updates DS when the pipeline of any linked-layer changes:
    private subscribeLinkedLayerPipelineExecutionChanges(scheme: DesignScheme): () => void {
        return usePersistentStore.subscribe(
            (s) =>
                this.getLinkedLayerExecutionSnapshot(
                    scheme,
                    s.layers.layerStates as Record<string, { pipeline?: { executionCount?: number } }>
                ),
            (next, previous) => {
                const prev = previous ?? {};
                const changedLayerIds = Object.keys(next).filter((layerId) => {
                    const curr = next[layerId];
                    const old = prev[layerId];
                    return !old || curr.executionCount !== old.executionCount;
                });
                this.refreshSchemeForChangedLayers(scheme, changedLayerIds);
            }
        );
    }

    private getLinkedLayerExecutionSnapshot(scheme: DesignScheme, layerStates: Record<string, { pipeline?: { executionCount?: number } }>) {
        const snapshot: Record<string, { executionCount: number | null }> = {};
        this.getWatchedLayerIds(scheme).forEach((layerId) => {
            snapshot[layerId] = {
                executionCount: layerStates[layerId]?.pipeline?.executionCount ?? null,
            };
        });
        return snapshot;
    }

    // Extras) Helper for layer changes:
    private refreshSchemeForChangedLayers(scheme: DesignScheme, changedLayerIds: string[]): void {
        if (changedLayerIds.length === 0 || scheme.isDisposed()) return;

        changedLayerIds.forEach((layerId) => {
            // 1. Linked Layer (MAIN/FACADE): Call onFrameChanged.
            if (scheme.isLayerLinked(layerId)) {
                scheme.onFrameChanged(layerId);
                return;
            }

            // 2. Component Layer: call update.
            scheme.update();
        });

        if (!scheme.isDisposed()) this.syncDerivedStateOnGenerationComplete(scheme);
    }

    private getWatchedLayerIds(scheme: DesignScheme): string[] {
        const ids = new Set<string>();
        ([LayerKind.MAIN, LayerKind.FACADE, LayerKind.COMPONENT] as const).forEach((kind) => {
            const layer = scheme.getLayer(kind);
            if (layer) ids.add(layer.getID());
        });

        if (scheme.getRendererKey() === SchemeKey.FLOOR_BASED) {
            const schemeState = this.getSchemeState(scheme);
            const componentLayerIds = schemeState?.floorBased?.facadeState?.componentLayerIds ?? {};
            Object.values(componentLayerIds).forEach((layerId) => {
                if (layerId) ids.add(layerId);
            });
        }

        return [...ids];
    }

    // ========================================
    // DERIVED STATE SYNCERS (e.g. Snap Angle)
    // ========================================

    // Coordinate Syncers on Generation Complete:
    private syncDerivedStateOnGenerationComplete(scheme: DesignScheme): void {
        const renderer = scheme.getRenderer();
        if (!renderer) return;

        renderer.onGenerationComplete(() => {
            if (scheme.isDisposed()) return;
            this.syncFloorBasedDerivedFacadeState(scheme);
            this.syncBoxDerivedState(scheme);
        });
    }

    // Vertical Angle and Vertical Height B Syncer (Both Derived values):
    private syncFloorBasedDerivedFacadeState(scheme: DesignScheme): void {
        if (scheme.getRendererKey() !== SchemeKey.FLOOR_BASED) return;

        const rendererConfig = scheme.getRenderer()?.getConfig() as FloorBasedDesignState | undefined;
        const schemeState = this.getSchemeState(scheme);
        const rendererFacadeState = rendererConfig?.facadeState;
        const storedFacadeState = schemeState?.floorBased?.facadeState;

        if (!rendererFacadeState || !storedFacadeState) return;

        const facadeStatePatch: DeepPartial<FloorBasedDesignState['facadeState']> = {};

        if (
            !rendererFacadeState.verticalCustomAngle &&
            storedFacadeState.verticalAngle?.value !== rendererFacadeState.verticalAngle.value
        ) {
            facadeStatePatch.verticalAngle = { value: rendererFacadeState.verticalAngle.value };
        }

        if (storedFacadeState.verticalHeightB !== rendererFacadeState.verticalHeightB) {
            facadeStatePatch.verticalHeightB = rendererFacadeState.verticalHeightB;
        }

        if (Object.keys(facadeStatePatch).length === 0) return;

        this.update({
            sets: {
                [scheme.getDesignSchemeSet().getID()]: {
                    footprints: {
                        [scheme.getFootprintId()]: {
                            designSchemes: {
                                [scheme.getID()]: {
                                    floorBased: {
                                        facadeState: facadeStatePatch,
                                    },
                                },
                            },
                        },
                    },
                },
            },
        });
    }

    // Box Angle Syncer (Derived value when SnapAngle is true):
    private syncBoxDerivedState(scheme: DesignScheme): void {
        if (scheme.getRendererKey() !== SchemeKey.BOX) return;

        const rendererConfig = scheme.getRenderer()?.getConfig() as BoxDesignState | undefined;
        const schemeState = this.getSchemeState(scheme);
        const storedBoxState = schemeState?.box;
        if (!rendererConfig || !storedBoxState) return;

        if (!rendererConfig.snapToVolume) return;
        if (!rendererConfig.alpha) return;

        const storedAlpha = storedBoxState.alpha?.value;
        const runtimeAlpha = rendererConfig.alpha.value;
        if (storedAlpha == null || runtimeAlpha == null) return;
        if (Math.abs(storedAlpha - runtimeAlpha) <= 1e-6) return;

        this.update({
            sets: {
                [scheme.getDesignSchemeSet().getID()]: {
                    footprints: {
                        [scheme.getFootprintId()]: {
                            designSchemes: {
                                [scheme.getID()]: {
                                    box: {
                                        alpha: {
                                            value: runtimeAlpha,
                                        },
                                    },
                                },
                            },
                        },
                    },
                },
            },
        });
    }
}
